import { Check, ChevronDown, X } from "lucide-react";
import { useId, useMemo, useRef, useState } from "react";

import { cn } from "~/lib/utils";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "./command";
import { Popover, PopoverAnchor, PopoverContent, PopoverTrigger } from "./popover";

export interface MultiSelectOption {
  id: string;
  label: string;
}

interface SearchableMultiSelectProps {
  /** Placeholder shown when nothing is selected. */
  placeholder: string;
  options: MultiSelectOption[];
  selectedIds: string[];
  onChange: (next: string[]) => void;
  /** Shown when no option matches the search (or there are no options). */
  emptyText: string;
  /** Placeholder for the search input inside the popover. */
  searchPlaceholder?: string;
  id?: string;
  "aria-labelledby"?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean;
}

/**
 * Searchable multi-select rendered in a portalled popover. Selected options show
 * as removable chips in the trigger; the popover lists every option with a typeahead
 * search box (cmdk filters by label) and a checkmark on the selected ones. Replaces
 * the per-feature `MultiSelectDropdown` copies, restoring the V1 Autocomplete search.
 */
export function SearchableMultiSelect({
  placeholder,
  options,
  selectedIds,
  onChange,
  emptyText,
  searchPlaceholder,
  id,
  "aria-labelledby": ariaLabelledBy,
  "aria-describedby": ariaDescribedBy,
  "aria-invalid": ariaInvalid,
}: SearchableMultiSelectProps) {
  const [open, setOpen] = useState(false);
  // Removing a chip reads as an outside interaction to Radix, so the close requests it fires
  // during that one pointer interaction have to be ignored.
  const suppressClose = useRef(false);
  const generatedId = useId();
  const triggerId = id ?? generatedId;

  const selectedOptions = useMemo(
    () => selectedIds.map((id) => options.find((o) => o.id === id)).filter((o): o is MultiSelectOption => Boolean(o)),
    [selectedIds, options],
  );

  const toggle = (id: string) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  const remove = (id: string) => onChange(selectedIds.filter((x) => x !== id));

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        if (!next && suppressClose.current) return;
        setOpen(next);
      }}
    >
      {/* Anchor the popover to the whole box so it keeps matching its width, not the trigger's. */}
      <PopoverAnchor asChild>
        {/* A label so the whole box opens the control, and because that forwarding is skipped for
            interactive descendants, the chips' remove buttons keep working. */}
        <label
          htmlFor={triggerId}
          className="flex min-h-8 w-full flex-wrap items-center gap-1 rounded-[var(--radius-sm)] border border-border-strong bg-white px-2 py-1 focus-within:ring-2 focus-within:ring-primary-300"
        >
          {/* Siblings of the trigger, not children: nested buttons are invalid and would swallow its keys. */}
          {selectedOptions.map((option) => (
            <span
              key={option.id}
              className="inline-flex max-w-full items-center gap-1 rounded-[var(--radius-pill)] bg-bg-subtle py-0.5 pl-2 pr-1 text-[11px] font-semibold text-fg-2"
            >
              <span className="truncate">{option.label}</span>
              <button
                type="button"
                aria-label={`remove ${option.label}`}
                // Radix looks for outside interactions on pointerdown, so the veto has to be up by
                // then. It drops a task after the pointer is released or cancelled: Radix asks to
                // close once more during that release, and an abandoned press never reaches the
                // click below, which would otherwise leave the veto up and wedge the popover open.
                onPointerDown={() => {
                  suppressClose.current = true;
                  const release = () => {
                    window.removeEventListener("pointerup", release);
                    window.removeEventListener("pointercancel", release);
                    setTimeout(() => {
                      suppressClose.current = false;
                    }, 0);
                  };
                  window.addEventListener("pointerup", release);
                  window.addEventListener("pointercancel", release);
                }}
                // Keeps focus where it is. This button unmounts on removal, and taking focus first
                // would strand it on <body> with the list still open.
                onMouseDown={(event) => event.preventDefault()}
                onClick={(event) => {
                  // Cancel the wrapping label's activation so removing does not also open the popover.
                  event.preventDefault();
                  suppressClose.current = false;
                  remove(option.id);
                }}
                className="shrink-0 rounded-full text-fg-3 hover:text-fg-1"
              >
                <X className="h-3 w-3" />
              </button>
            </span>
          ))}
          <PopoverTrigger asChild>
            <button
              type="button"
              id={triggerId}
              role="combobox"
              // `combobox` does not take its name from content, so the placeholder has to be the
              // fallback label; callers that own a real label pass `aria-labelledby` instead.
              aria-label={ariaLabelledBy ? undefined : placeholder}
              aria-expanded={open}
              aria-labelledby={ariaLabelledBy}
              aria-describedby={ariaDescribedBy}
              aria-invalid={ariaInvalid}
              aria-haspopup="listbox"
              className="flex min-w-0 flex-1 basis-16 cursor-pointer items-center gap-1 text-left focus:outline-none"
            >
              {selectedOptions.length === 0 ? <span className="text-fg-3">{placeholder}</span> : null}
              <ChevronDown className="ml-auto h-4 w-4 shrink-0 opacity-60" />
            </button>
          </PopoverTrigger>
        </label>
      </PopoverAnchor>
      <PopoverContent align="start" className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput placeholder={searchPlaceholder} />
          <CommandList>
            <CommandEmpty>{emptyText}</CommandEmpty>
            <CommandGroup>
              {options.map((option) => {
                const checked = selectedIds.includes(option.id);
                return (
                  <CommandItem
                    key={option.id}
                    value={option.id}
                    keywords={[option.label]}
                    onSelect={() => toggle(option.id)}
                  >
                    <Check className={cn("h-4 w-4 shrink-0", checked ? "opacity-100" : "opacity-0")} />
                    <span className="min-w-0 flex-1 truncate">{option.label}</span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
